import { DOCUMENT } from '@angular/common';
import { inject, Injectable, OnDestroy } from '@angular/core';

export type NativePermissionName = 'camera' | 'microphone' | 'geolocation';
type NativePermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported';

interface PermissionBridgeMessage {
  type: 'request' | 'check';
  name: NativePermissionName;
  requestId: string;
}

interface PermissionEventDetail {
  name: NativePermissionName;
  granted?: boolean;
  state?: NativePermissionState;
  requestId?: string;
}

const STATE_TIMEOUT_MS = 4000;
const REQUEST_TIMEOUT_MS = 120_000;

@Injectable({
  providedIn: 'root',
})
export class NativePermissionsService implements OnDestroy {
  readonly #document = inject(DOCUMENT);
  readonly #win: Window | null =
    this.#document?.defaultView ?? (typeof window !== 'undefined' ? window : null);
  readonly #bridgeAvailable = this.#win ? hasNativeBridge(this.#win) : false;
  readonly #requestResolvers = new Map<string, (detail: PermissionEventDetail) => void>();
  readonly #stateResolvers = new Map<string, (detail: PermissionEventDetail) => void>();
  #requestCounter = 0;

  readonly #resultListener = (event: Event) => {
    const detail = (event as CustomEvent<PermissionEventDetail>).detail;
    if (!detail?.requestId) {
      return;
    }
    const resolver = this.#requestResolvers.get(detail.requestId);
    if (resolver) {
      this.#requestResolvers.delete(detail.requestId);
      resolver(detail);
    }
  };

  readonly #stateListener = (event: Event) => {
    const detail = (event as CustomEvent<PermissionEventDetail>).detail;
    if (!detail?.requestId) {
      return;
    }
    const resolver = this.#stateResolvers.get(detail.requestId);
    if (resolver) {
      this.#stateResolvers.delete(detail.requestId);
      resolver(detail);
    }
  };

  constructor() {
    if (this.#bridgeAvailable && this.#win) {
      this.#win.addEventListener('wirePermissionResult', this.#resultListener as EventListener);
      this.#win.addEventListener('wirePermissionState', this.#stateListener as EventListener);
    }
  }

  ngOnDestroy(): void {
    if (this.#bridgeAvailable && this.#win) {
      this.#win.removeEventListener('wirePermissionResult', this.#resultListener as EventListener);
      this.#win.removeEventListener('wirePermissionState', this.#stateListener as EventListener);
    }
    this.#requestResolvers.clear();
    this.#stateResolvers.clear();
  }

  isBridgeAvailable(): boolean {
    return this.#bridgeAvailable;
  }

  async checkPermission(name: NativePermissionName): Promise<NativePermissionState> {
    if (!this.#bridgeAvailable || !this.#win) {
      return this.#fallbackPermissionQuery(name);
    }

    const requestId = this.#nextRequestId(name);
    const fallbackDetail: PermissionEventDetail = { name, state: 'unsupported', requestId };
    const detailPromise = this.#awaitDetail(
      this.#stateResolvers,
      fallbackDetail,
      requestId,
      STATE_TIMEOUT_MS
    );

    if (!this.#postNativeMessage({ type: 'check', name, requestId })) {
      this.#stateResolvers.delete(requestId);
      return fallbackDetail.state ?? 'unsupported';
    }

    const detail = await detailPromise;
    this.#log('state', name, detail);
    return detail.state ?? 'unsupported';
  }

  async requestPermission(name: NativePermissionName): Promise<boolean> {
    if (!this.#bridgeAvailable || !this.#win) {
      this.#log('result', name, { state: 'granted', granted: true });
      return true;
    }

    const requestId = this.#nextRequestId(name);
    const fallbackDetail: PermissionEventDetail = {
      name,
      granted: false,
      state: 'denied',
      requestId,
    };
    const detailPromise = this.#awaitDetail(
      this.#requestResolvers,
      fallbackDetail,
      requestId,
      REQUEST_TIMEOUT_MS
    );

    if (!this.#postNativeMessage({ type: 'request', name, requestId })) {
      this.#requestResolvers.delete(requestId);
      return fallbackDetail.granted ?? false;
    }

    const detail = await detailPromise;
    this.#log('result', name, detail);
    return detail.granted ?? false;
  }

  #nextRequestId(name: string): string {
    this.#requestCounter += 1;
    return `${name}-${Date.now()}-${this.#requestCounter}`;
  }

  #awaitDetail(
    map: Map<string, (detail: PermissionEventDetail) => void>,
    fallback: PermissionEventDetail,
    requestId: string,
    timeoutMs: number
  ): Promise<PermissionEventDetail> {
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        map.delete(requestId);
        resolve(fallback);
      }, timeoutMs);

      map.set(requestId, (detail) => {
        clearTimeout(timer);
        map.delete(requestId);
        resolve(detail);
      });
    });
  }

  #postNativeMessage(payload: PermissionBridgeMessage): boolean {
    if (!this.#win) {
      return false;
    }

    try {
      if (typeof this.#win.WireBridge?.postMessage === 'function') {
        this.#win.WireBridge.postMessage(JSON.stringify(payload));
        return true;
      }

      const handler = this.#win.webkit?.messageHandlers?.WireBridge;
      if (handler) {
        handler.postMessage(payload);
        return true;
      }
    } catch (error) {
      console.error('NativePermissionsService bridge error', error);
    }

    return false;
  }

  async #fallbackPermissionQuery(name: NativePermissionName): Promise<NativePermissionState> {
    if (
      typeof navigator === 'undefined' ||
      !('permissions' in navigator) ||
      !navigator.permissions?.query
    ) {
      this.#log('fallback', name, { state: 'prompt' });
      return 'prompt';
    }

    if (name !== 'geolocation') {
      return 'prompt';
    }

    try {
      const status = await navigator.permissions.query({ name: name as PermissionName });
      if (status.state === 'granted' || status.state === 'denied' || status.state === 'prompt') {
        this.#log('fallback', name, { state: status.state as NativePermissionState });
        return status.state as NativePermissionState;
      }
    } catch {
      // Ignore permission query failures and fall back to prompt state.
    }

    this.#log('fallback', name, { state: 'prompt' });
    return 'prompt';
  }

  #log(
    type: 'state' | 'result' | 'fallback',
    name: NativePermissionName,
    detail: PermissionEventDetail | { state: NativePermissionState; granted?: boolean }
  ): void {
    if (typeof console === 'undefined') {
      return;
    }

    const state = detail.state ?? (detail.granted ? 'granted' : 'denied');
    console.log(`[NativePermissions] ${name} ${type}: ${state}`, detail);
  }
}

const hasNativeBridge = (win: Window): boolean =>
  Boolean(
    typeof win.WireBridge?.postMessage === 'function' || win.webkit?.messageHandlers?.WireBridge
  );

declare global {
  interface Window {
    WireBridge?: {
      postMessage: (payload: string) => void;
    };
    webkit?: {
      messageHandlers?: {
        WireBridge?: {
          postMessage: (payload: unknown) => void;
        };
      };
    };
  }
}
